/* ============================================================================
   UI / menu — dropdown menus.
   useMenu (open / close / side logic), Menu (a button that opens a list), ContextMenu (a list at a point), MenuItem (one choice).
   ============================================================================ */
import React, { createContext, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";
import { Button } from "./actions.jsx";
import { cx } from "./util.js";

/* The open / close / side logic every dropdown shares: opens towards the side that has room, so the menu never
   leaves the screen (a phone, or a trigger near an edge); a click outside or Escape closes it and Escape hands
   the focus back to the trigger. */
export function useMenu() {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const root = useRef(null);
  const trigger = useRef(null);
  const menu = useRef(null);

  useLayoutEffect(() => {
    if (!open || !menu.current || !trigger.current) return;
    const t = trigger.current.getBoundingClientRect();
    const width = menu.current.offsetWidth;
    const roomRight = window.innerWidth - t.left - 8;
    const roomLeft = t.right - 8;
    setAlignRight(width > roomRight && roomLeft >= roomRight);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => { if (root.current && !root.current.contains(e.target)) setOpen(false); };
    const escape = (e) => { if (e.key === "Escape") { setOpen(false); trigger.current?.focus(); } };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("mousedown", outside); document.removeEventListener("keydown", escape); };
  }, [open]);

  return { open, setOpen, alignRight, root, trigger, menu };
}

const MenuContext = createContext(() => {});

/* A button that opens a list of choices (a MenuItem each), on the side that has room. Choosing one closes the list and
   hands the focus back to the button; Escape or a click outside does the same. `label` is the button's content and
   it takes Button's variant, size, leftIcon and aria-label; `scroll` caps a long list; `align="right"` forces the
   right edge. */
export function Menu({ label, align, scroll = false, className, menuClassName, menuLabel, children, ...buttonProps }) {
  const { open, setOpen, alignRight, root, trigger, menu } = useMenu();
  const menuId = useId();
  const close = () => { setOpen(false); trigger.current?.focus(); };
  return (
    <span className={cx("ds-menu-anchor", className)} ref={root}>
      <Button type="button" ref={trigger} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined} onClick={() => setOpen((v) => !v)} {...buttonProps}>{label}</Button>
      {open ? (
        <div id={menuId} ref={menu} role="menu" aria-label={menuLabel} className={cx("ds-menu", scroll && "ds-menu--scroll", (align === "right" || alignRight) && "ds-menu--right", menuClassName)}>
          <MenuContext.Provider value={close}>{children}</MenuContext.Provider>
        </div>
      ) : null}
    </span>
  );
}

/* A list of choices opened at a point of the page (a right-click on a row): `at` is { x, y } in window coordinates,
   null when closed. A click outside or Escape calls onClose, as does choosing an item. */
export function ContextMenu({ at, onClose, label, className, children }) {
  const panel = useRef(null);
  useEffect(() => {
    if (!at) return undefined;
    const outside = (e) => { if (panel.current && !panel.current.contains(e.target)) onClose?.(); };
    const escape = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("mousedown", outside); document.removeEventListener("keydown", escape); };
  }, [at, onClose]);
  if (!at) return null;
  return createPortal(
    <div ref={panel} role="menu" aria-label={label} className={cx("ds-menu", className)} style={{ left: at.x, top: at.y }} onContextMenu={(event) => event.preventDefault()}>
      <MenuContext.Provider value={() => onClose?.()}>{children}</MenuContext.Provider>
    </div>,
    document.body,
  );
}

/* One choice in a Menu. Pressing it runs onClick and closes the list (`keepOpen` leaves it open). With `checked` it is
   a tick choice (menuitemcheckbox), or a one-of-many choice with role="menuitemradio", and shows a tick when on. */
export function MenuItem({ checked, keepOpen = false, role, onClick, className, children, ...props }) {
  const close = useContext(MenuContext);
  const tick = checked !== undefined;
  return (
    <button type="button" role={role || (tick ? "menuitemcheckbox" : "menuitem")} aria-checked={tick ? checked : undefined} className={cx("ds-menu__item", className)}
      onClick={(event) => { onClick?.(event); if (!keepOpen) close(); }} {...props}>
      <span>{children}</span>
      {checked ? <Check size={14} aria-hidden="true" /> : null}
    </button>
  );
}
